import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import { useCommentStore } from "../../stores/commentStore.js";
import DisplayName from "../../models/DisplayName.js";
import * as Sentry from "@sentry/react";
import { useToast } from "../ToastWrapper/index.js";
import { sendAppEvent } from "../../Utils.js";
import { Button } from "../Button/index.js";

export function EditableDisplayName({ setShowTooltip, isMobile = false, setHideActions = () => {}, commentLocation }) {
  const { setToast } = useToast();
  const displayName = useCommentStore((state) => state.displayName);
  const setDisplayName = useCommentStore((state) => state.setDisplayName);
  const [isEditing, setIsEditing] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [oldDisplayName, setOldDisplayName] = useState(displayName); // in case of error
  const [placeholder, setPlaceholder] = useState("");
  const commenter = useCommentStore((state) => state.commenter);
  const documentId = useCommentStore((state) => state.documentId);


  // Force component to re-render when displayName changes
  useEffect(() => {
    if (isEditing) return;

    setNewDisplayName(displayName);
    setPlaceholder(displayName);
  }, [displayName]);


  const handleEditDisplayName = () => {
    setIsEditing(true);
    setPlaceholder(displayName);
    setNewDisplayName("");
    setHideActions(true);
  };

  const isAnonymousDisplayName = () => {
    return displayName?.startsWith("Anonymous");
  };

  const handleSendAppEvent = () => {
    sendAppEvent("CleanCopy - Update Display Name", {
      documentId,
      location: commentLocation,
      isAnonymous: commenter?.isAnonymous,
    });
  };

  const tryUpdateDisplayName = async (name) => {
    try {
      await DisplayName.update(name);
      handleSendAppEvent();
      setOldDisplayName(name);
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          func: "tryUpdateDisplayName",
        },
      });
      setNewDisplayName(oldDisplayName);
      setDisplayName(oldDisplayName);
      setToast("Error updating display name.");
    }
  };

  const handleSaveDisplayName = () => {
    setHideActions(false);
    const trimmedDisplayName = newDisplayName.trim();
    setNewDisplayName(trimmedDisplayName);

    if (trimmedDisplayName === "") {
      setToast("Display name cannot be empty");
      setNewDisplayName(displayName);
      return;
    }

    if (trimmedDisplayName !== displayName) {
      setDisplayName(trimmedDisplayName);
      tryUpdateDisplayName(trimmedDisplayName);
      setToast(`Display name updated to ${trimmedDisplayName}`);
    }
  };

  const handleTooltipVisibility = (show) => {
    if (isAnonymousDisplayName() && setShowTooltip) {
      setShowTooltip(show);
    }
  };

  return (
    <div className="commentAuthorContainer">
      {isEditing || isAnonymousDisplayName() ? (
        <>
          <DynamicInput
            type="text"
            autoFocus={isEditing}
            value={newDisplayName}
            placeholder={placeholder}
            onFocus={handleEditDisplayName}
            isMobile={isMobile}
            onChange={(e) => setNewDisplayName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSaveDisplayName();
                e.target.blur();
                setIsEditing(false);
              }
              if (e.key === "Escape") {
                setNewDisplayName(displayName);
                setIsEditing(false);
              }
            }}
            onBlur={() => {
              if (isEditing) {
                handleSaveDisplayName();
                setIsEditing(false);
              }
            }}
            onMouseEnter={() => handleTooltipVisibility(true)}
            onMouseLeave={() => handleTooltipVisibility(false)}
          />
          {isMobile && isEditing && (
            <Button
              small
              onClick={handleSaveDisplayName}
              style={{ marginLeft: "-7px" }}
            >
              Save
            </Button>
          )}
        </>
      ) : (
        <div
          className="commentAuthor"
          onClick={handleEditDisplayName}
        >
          {newDisplayName}
        </div>
      )}
    </div>
  );
}

export const DynamicInput = styled.input`
  border: 1.5px solid var(--grey-3);
  background-color: transparent;
  border-radius: 4px;
  line-height: 1.5;
  box-sizing: border-box;
  width: 100%;
  color: var(--grey-0);
  padding: 4px 4px 4px 8px;
  font-size: ${(props) => (props.isMobile ? "16px" : "14px")};

  &:focus {
    width: ${(props) => `${props.placeholder.length + 1}ch`};
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 100px;
    max-width: 100%;
    outline: none;
    border: 1.5px solid var(--primary-cta-high-contrast);
  }
`;
